import { get, range } from 'lodash'
import i18n from 'i18next'

import Vehicle from './Vehicle'

import {
    getEngineSizes,
    getVehicleDisplayName,
    getAlpahebeticalOptionGroups,
    getVehicleMakeQuickOptions
} from 'lib/utils/store/vehicle'

import getOptionsCarVehicleMake from 'graphql/options/getOptionsCarVehicleMake.gql'
import getOptionsCarVehicleModel from 'graphql/options/getOptionsCarVehicleModel.gql'
import getOptionsCarVehicleModelType from 'graphql/options/getOptionsCarVehicleModelType.gql'
import getOptionsCarVehicleCommon from 'graphql/options/getOptionsCarVehicleCommon.gql'
import getOptionsCarVehicleBodyType from 'graphql/options/getOptionsCarVehicleBodyType.gql'
import * as Nope from 'lib/utils/validation/Nope'
import * as Yup from 'yup'

const yearOptions = range(new Date().getFullYear() + 1, 1899).map(y => ({ name: y, value: y }))

export default class CarVehicle extends Vehicle {
    static create (vehicle) {
        return new CarVehicle(vehicle)
    }

    /**
     * Common data
     */

    getYear () {
        return get(this, 'vehicle.verticalData.year', null)
    }

    getMakeId () {
        return get(this, 'vehicle.verticalData.make.id', null)
    }

    getMakeName () {
        return get(this, 'vehicle.verticalData.make.name', null)
    }

    getModelId () {
        return get(this, 'vehicle.verticalData.model.id', null)
    }

    getModelName () {
        return get(this, 'vehicle.verticalData.model.name', null)
    }

    getMakeAndModelName () {
        const make = this.getMakeName() || ''
        const model = this.getModelName() || ''
        return `${make} ${model}`.trim()
    }

    getModelTypeId () {
        return get(this, 'vehicle.verticalData.modelType.id', null)
    }

    getModelTypeName () {
        return (
            get(this, 'vehicle.prefillData.modelTypeName', null) ||
            get(this, 'vehicle.verticalData.modelType.name', null)
        )
    }

    getMakeModelAndYear () {
        const year = this.getYear()
        const makeName = this.getMakeName()
        const modelName = this.getModelName()

        return (makeName || modelName || year)
            ? `${makeName || '-'}, ${modelName || '-'}, ${year || '-'}`
            : null
    }

    getBodyTypeId () {
        return get(this, 'vehicle.verticalData.bodyType.id', null)
    }

    getBodyTypeName (langCode) {
        return get(this, `vehicle.verticalData.bodyType[${langCode}]`, null)
    }

    getColorId () {
        return get(this, 'vehicle.verticalData.color.id', null)
    }

    getColorName (langCode) {
        return get(this, `vehicle.verticalData.color[${langCode}]`, null)
    }

    getColorTypeId () {
        return get(this, 'vehicle.verticalData.colorType.id', null)
    }

    getColorTypeName (langCode) {
        return get(this, `vehicle.verticalData.colorType[${langCode}]`, null)
    }

    /**
     * Technical data
     */

    getFuelTypeId () {
        return get(this, 'vehicle.verticalData.fuelType.id', null)
    }

    getFuelType (langCode) {
        return get(this, `vehicle.verticalData.fuelType[${langCode}]`, null)
    }

    getEngineSize () {
        return get(this, 'vehicle.verticalData.engineSize', null)
    }

    getDriveTypeId () {
        return get(this, 'vehicle.verticalData.driveType.id', null)
    }

    getDriveType (langCode) {
        return get(this, `vehicle.verticalData.driveType[${langCode}]`, null)
    }

    getGearTypeId () {
        return get(this, 'vehicle.verticalData.gearType.id', null)
    }

    getGearType (langCode) {
        return get(this, `vehicle.verticalData.gearType[${langCode}]`, null)
    }

    getPower () {
        return get(this, 'vehicle.verticalData.power', null)
    }

    getTorque () {
        return get(this, 'vehicle.verticalData.torque', null)
    }

    getPowerAndTorque () {
        const power = this.getPower()
        const torque = this.getTorque()
        return power || torque ? `${power ? `${power} (kW)` : '- '}, ${torque ? `${torque} (Nm)` : '-'}` : null
    }

    getTopSpeed () {
        return get(this, 'vehicle.verticalData.topSpeed', null)
    }

    getAcceleration () {
        return get(this, 'vehicle.verticalData.acceleration', null)
    }

    getTopSpeedAndAcceleration () {
        const topSpeed = this.getTopSpeed()
        const acceleration = this.getAcceleration()

        return topSpeed || acceleration
            ? `${topSpeed ? `${topSpeed} (km/h)` : '- '}, ${acceleration ? `${acceleration} s (0-100)` : '-'}`
            : null
    }

    getConsumptionCombined () {
        return get(this, 'vehicle.verticalData.consumptionCombined', null)
    }

    getConsumptionRoad () {
        return get(this, 'vehicle.verticalData.consumptionRoad', null)
    }

    getConsumptionUrban () {
        return get(this, 'vehicle.verticalData.consumptionUrban', null)
    }

    getConsumptionUrbanRoadCombined () {
        const urban = this.getConsumptionUrban()
        const road = this.getConsumptionRoad()
        const combined = this.getConsumptionCombined()

        return combined || road || urban
            ? (`${urban ? `${urban} (l/100km)` : '- '}, ${road ? `${road} (l/100km)` : '-'} , ${combined ? `${combined} (l/100km)` : '-'}`)
            : null
    }

    getDoors () {
        return get(this, 'vehicle.verticalData.doors', null)
    }

    getSeats () {
        return get(this, 'vehicle.verticalData.seats', null)
    }

    getCurbWeight () {
        return get(this, 'vehicle.verticalData.curbWeight', null)
    }

    getGrossWeight () {
        return get(this, 'vehicle.verticalData.grossWeight', null)
    }

    getTowWeightWithBrakes () {
        return get(this, 'vehicle.verticalData.towWeightWithBrakes', null)
    }

    getWeightCurbGrossTow () {
        const curb = this.getCurbWeight()
        const gross = this.getGrossWeight()
        const tow = this.getTowWeightWithBrakes()

        return curb || gross || tow
            ? `${curb ? `${curb} (kg)` : '- '}, ${gross ? `${gross} (kg)` : '- '} , ${tow ? `${tow} (kg)` : '-'}`
            : null
    }

    /**
     * Fields
     */

    fields = {
        name: (t) => ({
            type: 'string',
            inputType: 'text',
            fieldProps: {
                label: t('vehicleProfile:vehicleNickname'),
                description: t('vehicleProfile:vehicleNicknameDescription'),
                placeholder: getVehicleDisplayName(this.vehicle, t),
            },
            validation: Nope.vehicleName(),
            initialValue: this.getName(),
        }),
        make: (t) => ({
            type: 'string',
            inputType: 'select',
            fieldProps: {
                optionValueKey: 'id',
                label: t('vehicleProfile:vehicleMake'),
                emptyPlaceholder: t('vehicleProfile:sections.primary.empty.make'),
                loadingPlaceholder: t('vehicleProfile:sections.primary.loading.make'),
                placeholder: t('vehicleProfile:sections.primary.choose.make'),
            },
            resetFieldsOnChange: ['model', 'mfgYear', 'modelType', 'bodyType'],
            query: {
                query: getOptionsCarVehicleMake,
                variables: ({ values }) => {
                    return {
                        id: values.vehicleType
                    }
                },
                getResultValue: (data) => {
                    const mostPopular = getVehicleMakeQuickOptions(get(data, 'options.car.make', []), t)
                    const groupedMakes = getAlpahebeticalOptionGroups(get(data, 'options.car.make', []), 'name')
                    return [...mostPopular, ...groupedMakes]
                },
            },
            validation: Yup.string(),
            initialValue: this.getMakeId(),
        }),
        model: (t) => ({
            type: 'string',
            inputType: 'select',
            fieldProps: {
                optionValueKey: 'id',
                label: t('vehicleProfile:vehicleModel'),
                placeholder: t('vehicleProfile:sections.primary.choose.model'),
                loadingPlaceholder: t('vehicleProfile:sections.primary.loading.model'),
                emptyPlaceholder: t('vehicleProfile:sections.primary.empty.model'),
            },
            requireFields: ['make'],
            resetFieldsOnChange: ['mfgYear', 'modelType', 'bodyType'],
            query: {
                query: getOptionsCarVehicleModel,
                variables: ({ values }) => {
                    return {
                        id: parseInt(values.make)
                    }
                },
                getResultValue: (data) => {
                    return get(data, 'options.car.model', [])
                },
            },
            validation: Yup.string(),
            initialValue: this.getModelId(),
        }),
        mfgYear: (t) => ({
            type: 'string',
            inputType: 'select',
            fieldProps: {
                optionValueKey: 'value',
                label: t('vehicleProfile:vehicleYearModel'),
                placeholder: t('vehicleProfile:sections.primary.choose.year'),
                loadingPlaceholder: t('vehicleProfile:sections.primary.loading.year'),
                emptyPlaceholder: t('vehicleProfile:sections.primary.empty.year'),
                options: yearOptions,
            },
            requireFields: ['make', 'model'],
            resetFieldsOnChange: ['modelType'],
            validation: Yup.string().nullable(),
            initialValue: this.getYear(),
        }),
        modelType: (t) => ({
            type: 'string',
            inputType: 'select',
            fieldProps: {
                optionValueKey: 'id',
                label: t('vehicleProfile:vehicleModelType'),
                placeholder: t('vehicleProfile:sections.primary.choose.modelType'),
                loadingPlaceholder: t('vehicleProfile:sections.primary.loading.modelType'),
                emptyPlaceholder: t('vehicleProfile:sections.primary.empty.modelType'),
                options: this.getModelTypeName() ? [
                    {
                        id: this.getModelTypeName(),
                        name: this.getModelTypeName(),
                    }
                ] : [],
            },
            requireFields: ['model', 'mfgYear'],
            query: {
                query: getOptionsCarVehicleModelType,
                variables: ({ values }) => {
                    return {
                        id: parseInt(values.model),
                        year: parseInt(values.mfgYear),
                    }
                },
                getResultValue: (data) => {
                    return get(data, 'options.car.modelType', [])
                },
            },
            validation: Yup.string(),
            initialValue: this.getModelTypeId() || this.getModelTypeName(),
        }),
        bodyType: (t) => ({
            type: 'string',
            inputType: 'select',
            fieldProps: {
                optionNameKey: i18n.language,
                optionValueKey: 'id',
                label: t('vehicleProfile:bodyType'),
                placeholder: t('vehicleProfile:sections.primary.choose.bodyType'),
                loadingPlaceholder: t('vehicleProfile:sections.primary.loading.bodyType'),
                emptyPlaceholder: t('vehicleProfile:sections.primary.empty.bodyType'),
            },
            requireFields: ['make', 'model'],
            query: {
                query: getOptionsCarVehicleBodyType,
                variables: ({ values }) => {
                    return {
                        vehicleTypeId: parseInt(values.vehicleType),
                        makeId: parseInt(values.make),
                        modelId: parseInt(values.model)
                    }
                },
                getResultValue: (data) => {
                    return get(data, 'options.car.bodyType', [])
                },
            },
            validation: Yup.string(),
            initialValue: this.getBodyTypeId(),
        }),
        fuelType: (t) => ({
            type: 'string',
            inputType: 'select',
            fieldProps: {
                optionNameKey: i18n.language,
                optionValueKey: 'id',
                label: t('vehicleProfile:fuelType'),
                placeholder: t('vehicleProfile:sections.technical.choose.fuelType'),
                emptyPlaceholder: t('vehicleProfile:sections.technical.empty.fuelType'),
            },
            query: {
                query: getOptionsCarVehicleCommon,
                getResultValue: (data) => {
                    return get(data, 'options.car.fuelType', [])
                },
            },
            validation: Yup.string(),
            initialValue: this.getFuelTypeId(),
        }),
        color: (t) => ({
            type: 'string',
            inputType: 'select',
            fieldProps: {
                optionNameKey: i18n.language,
                optionValueKey: 'id',
                label: t('vehicleProfile:color'),
                placeholder: t('vehicleProfile:sections.primary.choose.color'),
                loadingPlaceholder: t('vehicleProfile:sections.primary.loading.color'),
                emptyPlaceholder: t('vehicleProfile:sections.primary.empty.color'),
            },
            query: {
                query: getOptionsCarVehicleCommon,
                getResultValue: (data) => {
                    return get(data, 'options.car.color', [])
                },
            },
            validation: Yup.string(),
            initialValue: this.getColorId(),
        }),
        colorType: (t) => ({
            type: 'string',
            inputType: 'select',
            fieldProps: {
                optionNameKey: i18n.language,
                optionValueKey: 'id',
                label: t('vehicleProfile:colorType'),
                placeholder: t('vehicleProfile:sections.primary.choose.colorType'),
                loadingPlaceholder: t('vehicleProfile:sections.primary.loading.colorType'),
                emptyPlaceholder: t('vehicleProfile:sections.primary.empty.colorType'),
            },
            query: {
                query: getOptionsCarVehicleCommon,
                getResultValue: (data) => {
                    return get(data, 'options.car.colorType', [])
                },
            },
            validation: Yup.string(),
            initialValue: this.getColorTypeId(),
        }),
    }

    /**
     * Accessories
     */

    getAccessories () {
        return get(this, 'vehicle.verticalData.accessories', null)
    }

    /**
     * Primary details
     */

    getPrimaryDetailsSummary () {
        return this.getMakeAndModelName() || '-'
    }

    getPrimaryDetailsValidationSchema (fields) {
        return Nope.schema({
            ...Vehicle.getFieldValidations(fields),
            vehicleType: Yup.string(),
            regNumber: Nope.registerNumber(),
        })
    }

    getPrimaryDetailsInitialValues (fields) {
        return {
            ...Vehicle.getFieldInitialValues(fields),
            vehicleType: this.getVehicleTypeId() || '',
            regNumber: this.getRegNumber() || '',
            isFinnishRegNumber: this.getIsFinnishRegNumber(),
        }
    }

    getPrimaryDetailsFieldsLayoutSchema () {
        return {
            layout: [
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['name'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['make', 'model', 'mfgYear', 'modelType', 'bodyType', 'fuelType'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['color', 'colorType'],
                },
            ]
        }
    }

    getPrimaryDetailsFieldsSchema (t) {
        return {
            fields: this.getFieldsForFieldSchema(t, [
                'name',
                'make',
                'model',
                'mfgYear',
                'modelType',
                'bodyType',
                'color',
                'colorType',
            ])
        }
    }

    /**
     * Technical details
     */
    getTechnicalDetailsValidationSchema () {
        return Nope.schema({
            power: Nope.positiveInteger(),
            torque: Nope.positiveInteger(),
            topSpeed: Nope.positiveInteger(),
            acceleration: Nope.positiveNumber(),
            seats: Nope.positiveInteger(),
            doors: Nope.positiveInteger(),
            consumptionCombined: Nope.positiveNumber(),
            consumptionRoad: Nope.positiveNumber(),
            consumptionUrban: Nope.positiveNumber(),
            curbWeight: Nope.positiveInteger(),
            grossWeight: Nope.positiveInteger(),
            towWeightWithBrakes: Nope.positiveInteger(),
            engineSize: Nope.positiveNumber(),
            fuelType: Nope.positiveInteger(),
            driveType: Nope.positiveInteger(),
            gearType: Nope.positiveInteger(),
        })
    }

    getTechnicalDetailsInitialValues () {
        const engineSize = this.getEngineSize()
        return {
            power: this.getPower() || '',
            torque: this.getTorque() || '',
            topSpeed: this.getTopSpeed() || '',
            acceleration: this.getAcceleration() || '',
            seats: this.getSeats() || '',
            doors: this.getDoors() || '',
            consumptionCombined: this.getConsumptionCombined() || '',
            consumptionRoad: this.getConsumptionRoad() || '',
            consumptionUrban: this.getConsumptionUrban() || '',
            curbWeight: this.getCurbWeight() || '',
            grossWeight: this.getGrossWeight() || '',
            towWeightWithBrakes: this.getTowWeightWithBrakes() || '',
            fuelType: this.getFuelTypeId() || '',
            gearType: this.getGearTypeId() || '',
            driveType: this.getDriveTypeId() || '',
            engineSize: engineSize || engineSize === 0
                ? engineSize.toString()
                : '',
        }
    }

    getTechnicalDetailsFieldsLayoutSchema (t) {
        return {
            layout: [
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['power', 'torque'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['topSpeed', 'acceleration'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['seats', 'doors'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['engineSize', 'fuelType'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['driveType', 'gearType'],
                },
                {
                    type: 'formElementGroup',
                    title: t('vehicleProfile:consumption'),
                    classNames: 'cols-3',
                    fields: ['consumptionUrban', 'consumptionRoad', 'consumptionCombined'],
                },
                {
                    type: 'formElementGroup',
                    title: t('vehicleProfile:weight'),
                    classNames: 'cols-3',
                    fields: ['curbWeight', 'grossWeight', 'towWeightWithBrakes'],
                },
            ]
        }
    }

    getTechnicalDetailsFieldsSchema (t) {
        return {
            fields: [
                {
                    name: 'power',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:power')} (kW)`,
                    },
                },
                {
                    name: 'torque',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:torque')} (Nm)`,
                    },
                },
                {
                    name: 'topSpeed',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:topSpeed')} (km/h)`,
                    },
                },
                {
                    name: 'acceleration',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:acceleration')} (0-100)`,
                    },
                },
                {
                    name: 'seats',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: t('vehicleProfile:seats'),
                    },
                },
                {
                    name: 'doors',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: t('vehicleProfile:doors'),
                    },
                },
                {
                    name: 'engineSize',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        label: t('vehicleProfile:engineSize'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.engineSize'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.engineSize'),
                        placeholder: t('vehicleProfile:sections.technical.choose.engineSize'),
                        options: [
                            { value: '0', name: t('vehicleProfile:sections.technical.noEngineSize') },
                            ...getEngineSizes(this.vehicle)
                        ]
                    },
                },
                {
                    name: 'fuelType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:fuelType'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.fuelType'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.fuelType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.fuelType'),
                    },
                    query: {
                        query: getOptionsCarVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.car.fuelType', [])
                        },
                    },
                },
                {
                    name: 'driveType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:driveType'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.driveType'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.driveType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.driveType'),
                    },
                    query: {
                        query: getOptionsCarVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.car.driveType', [])
                        },
                    },
                },
                {
                    name: 'gearType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:gearType'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.gearType'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.gearType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.gearType'),
                    },
                    query: {
                        query: getOptionsCarVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.car.gearType', [])
                        },
                    },
                },
                {
                    name: 'consumptionUrban',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:consumptionUrban')} (l/100km)`,
                    },
                },
                {
                    name: 'consumptionRoad',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:consumptionRoad')} (l/100km)`,
                    },
                },
                {
                    name: 'consumptionCombined',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:consumptionCombined')} (l/100km)`,
                    },
                },
                {
                    name: 'curbWeight',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:curbWeight')} (kg)`,
                    },
                },
                {
                    name: 'grossWeight',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:grossWeight')} (kg)`,
                    },
                },
                {
                    name: 'towWeightWithBrakes',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:towWeightWithBrakes')} (kg)`,
                    },
                },
            ],
        }
    }

    getPriceEstimateMandatoryFieldsSchema () {
        return [
            {
                name: 'make',
                required: true,
            },
            {
                name: 'model',
                required: true,
            },
            {
                name: 'mfgYear',
                required: true,
            },
            {
                name: 'bodyType',
                required: false,
            },
            {
                name: 'fuelType',
                required: false,
            }
        ]
    }
}
